import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import * as grid from '@helpcenter/styles/src/grid';
import * as mixins from '@helpcenter/styles/src/mixins';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { UnstyledButton } from 'view/common/unstyled-button';
import { IconCardLayout } from 'view/layout/icon-card-layout';
import CrossIcon from '@atlaskit/icon/core/cross';
import FileIcon from '@atlaskit/icon/core/file';
import { token } from '@atlaskit/tokens';
import i18n from '../messages';
import { UploadProgressBarInProgress, UploadProgressBarFailure } from './styled';

export interface UploadStatusProps {
	id: string;
	name: string;
	mimeType: string;
	size: number;
	publicId?: string;
	preview?: string;
	progress?: number;
	failed?: boolean;
	error?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onRemove?: () => any;
}

export class UploadStatus extends React.PureComponent<UploadStatusProps & WrappedComponentProps> {
	renderProgressBar() {
		if (this.props.failed) {
			return (
				<UploadProgressBarContainer>
					<UploadProgressBarFailure />
				</UploadProgressBarContainer>
			);
		}
		const progress = this.props.progress || 0;
		return (
			<UploadProgressBarContainer>
				<UploadProgressBarInProgress progress={progress} />
			</UploadProgressBarContainer>
		);
	}

	onRemove = () => {
		this.props.onRemove && this.props.onRemove();
	};

	renderRightIcon() {
		const iconProps = {
			label: this.props.intl.formatMessage(i18n.attachmentRemoveLabel),
			primaryColor: token('color.icon', '#42526E'),
		};
		return (
			<UnstyledButton onClick={this.onRemove} actionSubjectId="removeAttachment">
				<CrossIcon {...iconProps} size="medium" spacing="spacious" />
			</UnstyledButton>
		);
	}

	render() {
		const { name, preview, failed, error, intl } = this.props;
		const icon = preview ? (
			<UploadStatusPreview alt={intl.formatMessage(i18n.attachmentRemoveLabel)} src={preview} />
		) : (
			<FileIcon
				label={intl.formatMessage(i18n.attachmentRemoveLabel)}
				size="medium"
				spacing="spacious"
			/>
		);
		const content = (
			<React.Fragment>
				<AttachmentContainer>
					<AttachmentFileName title={name}>{name}</AttachmentFileName>
					{failed && (
						<AttachmentMessage title={error}>
							{intl.formatMessage(i18n.attachmentUploadFailed)}
						</AttachmentMessage>
					)}
				</AttachmentContainer>
				{this.renderProgressBar()}
			</React.Fragment>
		);
		return <IconCardLayout leftIcon={icon} content={content} rightIcon={this.renderRightIcon()} />;
	}
}

export default injectIntl(UploadStatus);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const AttachmentContainer = styled.div`
	margin-top: ${token('space.100', '8px')};
	display: flex;
	flex-flow: row no-wrap;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const AttachmentFileName = styled.div`
	${mixins.clamp}
	margin-bottom: ${token('space.100', '8px')};
	flex: 1 1 auto;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const AttachmentMessage = styled.div`
	flex: 0 0 auto;
	margin-left: ${token('space.100', '8px')};
	white-space: nowrap;
	color: ${token('color.text.subtlest', '#5E6C84')};
`;

const UPLOAD_STATUS_ICON_SIZE = grid.multiple(4).px;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const UploadStatusPreview = styled.img`
	height: ${UPLOAD_STATUS_ICON_SIZE};
	width: ${UPLOAD_STATUS_ICON_SIZE};
`;

// Progress Bar - Replace with AtlasKit version when available
const progressBarHeight = '6px';

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const UploadProgressBarContainer = styled.div`
	${mixins.borderRadius};
	margin-bottom: ${token('space.100', '8px')};
	height: ${progressBarHeight};
	width: 100%;
	background: ${token('color.background.neutral', 'rgba(9, 30, 66, 0.08)')};
`;
